'use client';

import React from 'react';
import { IconRentStabilized, IconGroup, IconDocument, IconChevronRight } from '@repo/svgs';
import { Divider } from '@/components/ui/Divider';
import { useBuilding } from '@/app/building/context/BuildingProvider';

interface RentStabilizedItemProps {
  icon: React.ElementType;
  title: string;
  description: React.ReactNode;
  hasLink?: boolean;
  onLinkClick?: () => void;
}

const RentStabilizedItem = ({
  icon: Icon,
  title,
  description,
  hasLink = false,
  onLinkClick,
}: RentStabilizedItemProps) => (
  <div className="flex gap-4 items-start px-2 py-3">
    <Icon className="w-6 h-6 text-success mt-0.5 shrink-0" />
    <div className="flex-1 flex flex-col gap-1">
      <p className="text-body-2 text-primary">{title}</p>
      <div className="text-caption text-secondary">{description}</div>
      {hasLink && (
        <button onClick={onLinkClick} className="flex items-center gap-1 mt-2 group">
          <span className="text-body-2-high text-accent">Learn more</span>
          <IconChevronRight className="w-3 h-3 text-accent transition-transform group-hover:translate-x-0.5" />
        </button>
      )}
    </div>
  </div>
);

// Description mapping for common rent stabilized statuses
const getDescription = (description: string): React.ReactNode => {
  const descLower = description.toLowerCase();

  // 421-A Tax Incentive
  if (descLower.includes('421-a')) {
    return (
      <div>The 421-A Tax Incentive is a partial tax exemption for new multiple dwellings. </div>
    );
  }

  // 421-G Tax Incentive
  if (descLower.includes('421-g')) {
    return (
      <div>
        The 421-G Tax Incentive is a tax exemption and abatement for conversion of commercial
        buildings to multiple dwellings in Downtown Manhattan. 421-G is not available to projects
        that commenced conversion after 2006.
      </div>
    );
  }

  // Multiple Dwelling A
  if (descLower.includes('multiple dwelling') && descLower.includes('a')) {
    return (
      <div>
        As defined under the Multiple Dwelling Law, a multiple dwelling building which is generally
        occupied as a permanent residence. The class includes such buildings as apartment houses,
        apartment hotels, maisonette apartments, and all other multiple dwellings except class B
        dwellings.
      </div>
    );
  }

  // Default description with links
  return (
    <div>
      Learn more about rent-stabilized units on{' '}
      <a
        rel="noreferrer"
        target="_blank"
        className="text-accent underline hover:opacity-80"
        href="https://rentguidelinesboard.cityofnewyork.us"
      >
        NYC Rent Guidelines Board
      </a>{' '}
      or{' '}
      <a
        rel="noreferrer"
        target="_blank"
        className="text-accent underline hover:opacity-80"
        href="https://hcr.ny.gov/rent-regulation-fact-sheets"
      >
        Rent Regulation Fact Sheets
      </a>{' '}
      from Office of Rent Administration of New York State.
    </div>
  );
};

const titleCase = (str: string): string => {
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export function RentStabilizedModal() {
  const { building } = useBuilding();
  const rentStabilizedRecords = building.openData?.rentStabilizedRecords || [];
  const isCommunityReported = building.isRentStabilized || false;

  // Only show records that exist
  const hasRecords = rentStabilizedRecords.length > 0;

  return (
    <div className="flex flex-col gap-4">
      {/* Header Section */}
      <div className="flex flex-col gap-4 px-2">
        <IconRentStabilized className="w-8 h-8 text-icon-rent-stabilized" />
        <p className="text-body-2 text-primary">
          Data suggests that this building has rent-stabilized apartments.
        </p>
      </div>

      {/* List Section */}
      <div className="flex flex-col gap-1">
        {/* Community Reported - only show if building.isRentStabilized is true */}
        {isCommunityReported && (
          <>
            <RentStabilizedItem
              icon={IconGroup}
              title="Openigloo community reported"
              description="An Openigloo user has reported this building has at least one rent-stabilized unit."
            />
            {hasRecords && (
              <div className="px-2">
                <Divider />
              </div>
            )}
          </>
        )}

        {/* DHCR Records - only show records that exist */}
        {hasRecords &&
          rentStabilizedRecords.map((record, index) => {
            const status = record.status?.toUpperCase() || '';
            const description = record.description || '';
            const title = `DHCR ${status}: ${titleCase(description)}`;
            const has421ALink = description.toLowerCase().includes('421-a');

            return (
              <React.Fragment key={index}>
                <RentStabilizedItem
                  icon={IconDocument}
                  title={title}
                  description={getDescription(description)}
                  hasLink={has421ALink}
                  onLinkClick={() => {
                    window.open(
                      'https://www.nyc.gov/site/hpd/services-and-information/tax-incentives-421-a.page',
                      '_blank',
                    );
                  }}
                />
                {index < rentStabilizedRecords.length - 1 && (
                  <div className="px-2">
                    <Divider />
                  </div>
                )}
              </React.Fragment>
            );
          })}
      </div>

      {/* Info Container */}
      <div className="px-2">
        <div className="bg-secondary rounded-2xl p-4 flex flex-col gap-2">
          <p className="text-body-2 text-secondary">
            All landlords in NYC that manage rent-stabilized units are required to register the
            units every year with Division of Housing and Community Renewal (DHCR).
          </p>
          <p className="text-body-2 text-secondary">
            Please note that we are unable to confirm which units are stabilized but are simply
            sharing that records show the building has some stabilized units. Some buildings may
            have a combination of both market-rate and regulated apartments.
          </p>
        </div>
      </div>
    </div>
  );
}
